import { useEffect, useRef } from 'react';
import { StyledCodeInputRow } from './index.styled';

interface Props {
  digits: string[];
  onChange: (digits: string[]) => void;
  onComplete?: (code: string) => void;
  status: 'idle' | 'verifying' | 'error';
  autoFocus?: boolean;
}

/**
 * Six single-character boxes, per the design handoff.
 *
 * The fiddly parts are all keyboard behaviour: typing advances, Backspace in an
 * empty box steps back, arrows move, and a paste anywhere distributes across
 * the row — which is how most people actually enter a code copied from a
 * password manager.
 */
const CodeInput = ({
  digits,
  onChange,
  onComplete,
  status,
  autoFocus,
}: Props) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const commit = (next: string[]) => {
    onChange(next);
    const joined = next.join('');
    if (joined.length === 6 && !next.includes('')) onComplete?.(joined);
  };

  const handleChange = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '');
    if (!value) return;
    const next = [...digits];
    next[index] = value[value.length - 1];
    commit(next);
    if (index < 5) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        commit(next);
        return;
      }
      if (index > 0) {
        next[index - 1] = '';
        commit(next);
        refs.current[index - 1]?.focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (
    index: number,
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();

    const next = [...digits];
    let cursor = index;
    for (const char of pasted.slice(0, 6 - index)) {
      next[cursor] = char;
      cursor += 1;
    }
    commit(next);
    refs.current[Math.min(cursor, 5)]?.focus();
  };

  return (
    <StyledCodeInputRow $error={status === 'error'}>
      {digits.map((digit, index) => (
        <input
          // Position is the identity here — the boxes never reorder.
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          className={digit ? 'filled' : ''}
          value={digit}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={status === 'verifying'}
          aria-label={`Digit ${index + 1} of 6`}
          // A rejected code was silent for a screen reader (Victor, review of
          // #1755): the boxes emptied and the caret moved, both invisible without
          // sight. This marks the field, and the hint that carries the wording is
          // a live region — see `role="alert"` in TwoFactorVerify.
          aria-invalid={status === 'error'}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
        />
      ))}
    </StyledCodeInputRow>
  );
};

export default CodeInput;
